import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../common';
import { Heading, Typography } from '../../common/Typography';
import { Card } from '../../common/Card';
import { homeSectionHeading } from '../../home/homeDashboardStyles';
import { FrequentMatchupCard } from '../../matchups/FrequentMatchupCard';
import { interactiveBleedInsetX } from '../../../constants/interactiveSafeInsets';

const clubMatchupsStripClass = `flex gap-4 overflow-x-auto pb-3 pt-1 ${interactiveBleedInsetX} scroll-smooth snap-x snap-mandatory sm:snap-none [scrollbar-gutter:stable_both-edges] touch-pan-x scrollbar-hide-mobile`;

/**
 * Club-scoped frequent matchups — horizontal snap carousel on mobile (matches homepage).
 *
 * @param {boolean} [embedded=false] - When true, omits the outer H2 heading so the carousel can be
 *   used inside ClubActivitySection alongside ClubMomentsStrip.
 */
export function ClubFrequentMatchups({ matchups, loading, onViewMatchups, embedded = false, clubId = null }) {
  const navigate = useNavigate();

  function handleViewHistory(matchup) {
    const params = new URLSearchParams({ opponentId: matchup.opponent_id });
    const resolvedClubId = matchup.club_id || clubId;
    if (resolvedClubId) params.set('clubId', resolvedClubId);
    navigate(`/profile/matchups?${params.toString()}`);
  }

  if (loading) {
    if (embedded) return null;
    return (
      <div>
        <Heading level={2} className={`${homeSectionHeading} mb-5`}>Matchups</Heading>
        <div className="text-center py-8 rounded-2xl border border-border-medium bg-surface-subtle/60">
          <Typography variant="muted">Loading matchups...</Typography>
        </div>
      </div>
    );
  }

  if (!matchups || matchups.length === 0) return null;

  if (embedded) {
    return (
      <>
        <div
          role="region"
          aria-label="Matchups"
          className={clubMatchupsStripClass}
          onClick={(e) => e.stopPropagation()}
        >
          {matchups.slice(0, 3).map((matchup) => (
            <FrequentMatchupCard
              key={`${matchup.opponent_id}-${matchup.club_id}-${matchup.season_id}`}
              matchup={matchup}
              onViewHistory={handleViewHistory}
              compact
            />
          ))}
        </div>
        {matchups.length > 1 && (
          <Typography variant="caption" className="mt-3 text-center text-text-muted px-2 sm:hidden">
            Swipe for more
          </Typography>
        )}
      </>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3">
        <Heading level={2} className={homeSectionHeading}>Matchups</Heading>
        <Button
          onClick={onViewMatchups}
          variant="text"
          className="text-sm shrink-0 font-semibold text-primary"
        >
          View all
        </Button>
      </div>

      <div
        role="region"
        aria-label="Matchups"
        className={clubMatchupsStripClass}
      >
        {matchups.slice(0, 3).map((matchup) => (
          <FrequentMatchupCard
            key={`${matchup.opponent_id}-${matchup.club_id}-${matchup.season_id}`}
            matchup={matchup}
            onViewHistory={handleViewHistory}
          />
        ))}
      </div>

      {matchups.length > 1 ? (
        <Typography variant="caption" className="mt-3 text-center text-text-muted px-2 sm:hidden">
          Swipe sideways for more · Use{' '}
          <span className="font-semibold text-text-secondary">View match history</span> on each card
        </Typography>
      ) : null}
    </div>
  );
}
